import { createContext, useContext } from "react";

export type AuthUser = {
  id: string;
  email: string;
  name: string;
  nickname?: string | null;
  image?: string | null;
  isAdmin?: boolean;
  isAnonymous?: boolean;
};

export type SocialProvider = "github" | "google";

export type AuthContextValue = {
  user: AuthUser | null;
  setUser?: (user: AuthUser) => void;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  signOut: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  signInWithSocial: (provider: SocialProvider, returnTo?: string) => Promise<void>;
  resendVerificationEmail: (email: string) => Promise<void>;
  signInAnonymous: () => Promise<AuthUser>;
};

export const AuthContext = createContext<AuthContextValue>({
  user: null,
  isLoading: false,
  signIn: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  requestPasswordReset: async () => {},
  resetPassword: async () => {},
  signInWithSocial: async () => {},
  resendVerificationEmail: async () => {},
  signInAnonymous: async () => ({ id: "", email: "", name: "" }),
});

export const useAuth = () => useContext(AuthContext);

export function isRealUser(user: AuthUser | null): user is AuthUser {
  return !!user && !user.isAnonymous;
}
